import { useState, useReducer, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { Checklist } from '@/components/ui/Checklist';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { EmptyState } from '@/components/ui/EmptyState';
import { Alert } from '@/components/ui/Alert';
import type { MerchantSummary, MerchantDetail, Campus, MarketplaceRoleOption } from '@/types';
import styles from './AdminMarketplacePage.module.css';

/* ─── Onboarding wizard state ─────────────────────────────────────── */

interface WizardState {
  step: number;
  businessName: string;
  legalName: string;
  merchantType: 'INSTITUTION_SPONSORED' | 'PLATFORM';
  businessRegNumber: string;
  taxReference: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  contactName: string;
  selectedCampuses: string[];
  submitting: boolean;
  error: string | null;
}

type WizardAction =
  | { type: 'SET_FIELD'; field: keyof WizardState; value: string }
  | { type: 'SET_CAMPUSES'; campuses: string[] }
  | { type: 'NEXT' }
  | { type: 'BACK' }
  | { type: 'SUBMITTING' }
  | { type: 'DONE' }
  | { type: 'ERROR'; error: string }
  | { type: 'RESET' };

const INITIAL_WIZARD: WizardState = {
  step: 1,
  businessName: '',
  legalName: '',
  merchantType: 'INSTITUTION_SPONSORED',
  businessRegNumber: '',
  taxReference: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'Cameroon',
  contactName: '',
  selectedCampuses: [],
  submitting: false,
  error: null,
};

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_CAMPUSES':
      return { ...state, selectedCampuses: action.campuses };
    case 'NEXT':
      return { ...state, step: Math.min(state.step + 1, 4) };
    case 'BACK':
      return { ...state, step: Math.max(state.step - 1, 1) };
    case 'SUBMITTING':
      return { ...state, submitting: true, error: null };
    case 'DONE':
      return { ...state, submitting: false };
    case 'ERROR':
      return { ...state, submitting: false, error: action.error };
    case 'RESET':
      return { ...INITIAL_WIZARD };
    default:
      return state;
  }
}

/** [countryName, countryCode, [[regionName, regionCode], ...]] — the shape
 *  country-region-data ships. Stored as names because the merchant profile
 *  persists display strings, not ISO codes. */
type CountryEntry = readonly [string, string, ReadonlyArray<readonly [string, string]>];

const STATUS_TONE: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'info',
  PROVISIONING: 'info',
  ACTIVE: 'success',
  REJECTED: 'danger',
  SUSPENDED: 'danger',
  CLOSED: 'neutral',
};

/* ─── Plugin ──────────────────────────────────────────────────────── */

export function AdminMarketplacePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state: merchants, reload: reloadMerchants } = useAsyncData<MerchantSummary[]>(
    'admin-marketplace-merchants',
    useCallback(() => api.admin.marketplace.getMerchants(institutionId), [institutionId]),
    !!institutionId,
  );

  const { state: campuses } = useAsyncData<Campus[]>(
    'admin-marketplace-campuses',
    useCallback(() => api.admin.campuses.list(institutionId), [institutionId]),
    !!institutionId,
  );

  const { state: config, reload: reloadConfig } = useAsyncData<import('@/types').MarketplaceConfig>(
    'admin-marketplace-config',
    useCallback(() => api.admin.marketplace.get(institutionId), [institutionId]),
    !!institutionId,
  );

  const [showWizard, setShowWizard] = useState(false);
  const [wizard, dispatch] = useReducer(wizardReducer, INITIAL_WIZARD);

  // The country/region dataset (~130 KB) is loaded only once the wizard opens,
  // so browsing merchants never pays for it.
  const [countries, setCountries] = useState<ReadonlyArray<CountryEntry> | null>(null);
  useEffect(() => {
    if (!showWizard || countries) return;
    let active = true;
    import('country-region-data').then((mod) => {
      if (active) setCountries(mod.allCountries as unknown as ReadonlyArray<CountryEntry>);
    });
    return () => {
      active = false;
    };
  }, [showWizard, countries]);

  const countryOptions = useMemo(
    () => (countries ?? []).map(([name]) => ({ value: name, label: name })),
    [countries],
  );
  const regionOptions = useMemo(() => {
    const entry = (countries ?? []).find(([name]) => name === wizard.country);
    if (!entry) return [];
    return entry[2].map(([name]) => ({ value: name, label: name }));
  }, [countries, wizard.country]);
  /* ── Marketplace team invites ── */
  const [inviteOpen, setInviteOpen] = useState(false);
  // Latched on first open so the role list is fetched lazily, once, and survives
  // the modal closing (reopening should not refetch).
  const [rolesRequested, setRolesRequested] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', roleCode: '', merchantId: '' });
  const [inviteEmailError, setInviteEmailError] = useState('');
  const [inviting, setInviting] = useState(false);

  const emailLooksValid = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

  const { state: roleOptions } = useAsyncData<MarketplaceRoleOption[]>(
    'admin-marketplace-roles',
    useCallback(() => api.admin.marketplace.listRoles(institutionId), [institutionId]),
    rolesRequested && !!institutionId,
  );
  const inviteRoles = roleOptions.status === 'ready' ? roleOptions.data : [];
  const selectedInviteRole = inviteRoles.find((r) => r.code === inviteForm.roleCode);
  const merchantRequired = selectedInviteRole?.scope === 'MERCHANT';
  const merchantList = merchants.status === 'ready' ? merchants.data : [];
  const inviteReady =
    emailLooksValid(inviteForm.email) &&
    !!inviteForm.roleCode &&
    (!merchantRequired || !!inviteForm.merchantId);

  const openInvite = () => {
    setInviteForm({ email: '', roleCode: '', merchantId: '' });
    setInviteEmailError('');
    setRolesRequested(true);
    setInviteOpen(true);
  };

  const [inviteShareLink, setInviteShareLink] = useState<{ email: string; url: string } | null>(
    null,
  );

  const handleSendInvite = async () => {
    if (!institutionId || !inviteReady || inviting) return;
    setInviting(true);
    try {
      const body: { email: string; roleCode: string; merchantId?: string; merchantName?: string } = {
        email: inviteForm.email.trim(),
        roleCode: inviteForm.roleCode,
      };
      if (merchantRequired) {
        body.merchantId = inviteForm.merchantId;
        const merchant = merchantList.find((m) => m.id === inviteForm.merchantId);
        if (merchant) body.merchantName = merchant.businessName;
      }
      const result = await api.admin.marketplace.inviteRole(institutionId, body);
      setInviteOpen(false);
      if (result.delivered) {
        toast.success(t('admin.marketplace.inviteSent', { email: body.email }));
      } else {
        // Mail could not be sent; identity handed back the exact link so the
        // administrator can deliver it by hand instead of hitting a dead end.
        if (result.inviteUrl) setInviteShareLink({ email: body.email, url: result.inviteUrl });
        toast.info(t('admin.marketplace.inviteNotDelivered', { email: body.email }));
      }
    } catch {
      // The modal stays open with the form intact for a retry.
      toast.error(t('common.actionFailed'));
    } finally {
      setInviting(false);
    }
  };

  const [detailMerchantId, setDetailMerchantId] = useState<string | null>(null);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toggleLoading, setToggleLoading] = useState(false);

  const { state: detail, reload: reloadDetail } = useAsyncData<MerchantDetail | null>(
    detailMerchantId ? `merchant-detail-${detailMerchantId}` : '',
    useCallback(() => api.admin.marketplace.getMerchantDetail(institutionId, detailMerchantId!), [institutionId, detailMerchantId]),
    !!detailMerchantId,
  );

  const handleSubmitOnboarding = async () => {
    if (!institutionId) return;
    dispatch({ type: 'SUBMITTING' });
    try {
      await api.admin.marketplace.createMerchant(institutionId, {
        businessName: wizard.businessName,
        legalName: wizard.legalName,
        merchantType: wizard.merchantType,
        businessRegNumber: wizard.businessRegNumber || undefined,
        taxReference: wizard.taxReference || undefined,
        email: wizard.email,
        phone: wizard.phone,
        campusIds: wizard.selectedCampuses,
      });
      dispatch({ type: 'DONE' });
      setShowWizard(false);
      reloadMerchants();
    } catch (err) {
      dispatch({ type: 'ERROR', error: (err as Error).message });
    }
  };

  const handleToggle = async () => {
    if (!institutionId || toggleLoading) return;
    setToggleLoading(true);
    const enabling = !config.data?.enabled;
    try {
      // Only the field being changed: the endpoint merges per-field, and the
      // fetched object carries server-side columns the DTO rejects.
      await api.admin.marketplace.update(institutionId, { enabled: enabling });
      await reloadConfig();
      toast.success(
        t(enabling ? 'admin.marketplace.enabledToast' : 'admin.marketplace.disabledToast'),
      );
    } catch {
      toast.error(t('common.saveFailed'));
    } finally {
      setToggleLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!institutionId || !detailMerchantId || !ownerEmail.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.admin.marketplace.approveMerchant(institutionId, detailMerchantId, ownerEmail.trim());
      setOwnerEmail('');
      await reloadDetail();
      await reloadMerchants();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!institutionId || !detailMerchantId || !rejectReason.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await api.admin.marketplace.rejectMerchant(institutionId, detailMerchantId, rejectReason.trim());
      setRejectReason('');
      await reloadDetail();
      await reloadMerchants();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  const openDetail = (id: string) => {
    setDetailMerchantId(id);
    setOwnerEmail('');
    setRejectReason('');
    setActionError(null);
  };

  const closeDetail = () => {
    setDetailMerchantId(null);
  };

  if (!activeContext) return null;

  return (
    <div className={styles.page}>
      <h1>{t('admin.marketplace.title')}</h1>
      <p className={styles.subtitle}>{t('admin.marketplace.subtitle')}</p>

      {/* ── Settings cards ── */}
      <div className={styles.contentGrid}>
        <Card className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('admin.marketplace.enableMarketplace')}</h2>
            <button
              className={`${styles.toggle} ${config.data?.enabled ? styles.toggleActive : ''}`}
              onClick={handleToggle}
              disabled={toggleLoading}
            >
              <span className={styles.toggleKnob} />
            </button>
          </div>
          <p className={styles.toggleLabel}>{t('admin.marketplace.enableMarketplaceLabel')}</p>
        </Card>

        <Card className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('admin.marketplace.configureCategories')}</h2>
          </div>
          <ul className={styles.categoryList}>
            {config.data?.allowedCategories?.map((cat: string) => (
              <li key={cat} className={styles.categoryItem}>
                <span className={styles.categoryDot} />
                {cat}
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* ── Marketplace team ── */}
      <Card className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('admin.marketplace.teamTitle')}</h2>
          <Button variant="primary" size="sm" onClick={openInvite}>
            {t('admin.marketplace.inviteRole')}
          </Button>
        </div>
        <p className={styles.toggleLabel}>{t('admin.marketplace.teamSubtitle')}</p>
      </Card>

      {/* ── Merchants table ── */}
      <Card className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('admin.marketplace.merchants')}</h2>
          <Button variant="primary" size="sm" onClick={() => { setShowWizard(true); dispatch({ type: 'RESET' }); }}>
            {t('admin.marketplace.onboardMerchant')}
          </Button>
        </div>

        {merchants.status === 'loading' && (
          <div className={styles.loading}>
            <Spinner label={t('admin.marketplace.loading')} />
          </div>
        )}

        {merchants.status === 'ready' && merchants.data.length === 0 && (
          <EmptyState
            title={t('admin.marketplace.noMerchants')}
            description={t('admin.marketplace.noMerchants')}
          />
        )}

        {merchants.status === 'ready' && merchants.data.length > 0 && (
          <div className="table-scroll">
            <table className={styles.merchantTable}>
            <thead>
              <tr>
                <th>{t('admin.marketplace.merchantName')}</th>
                <th>{t('admin.marketplace.merchantType')}</th>
                <th>{t('admin.marketplace.approvalStatus')}</th>
                <th>{t('admin.marketplace.createdAt')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {merchants.data.map((m) => (
                <tr key={m.id}>
                  <td>{m.businessName}</td>
                  <td className={styles.merchantType}>
                    {m.merchantType === 'INSTITUTION_SPONSORED'
                      ? t('admin.marketplace.institutionSponsored')
                      : t('admin.marketplace.platform')}
                  </td>
                  <td>
                    <Badge tone={STATUS_TONE[m.status] ?? 'neutral'}>
                      {m.status === 'ACTIVE' ? t('admin.marketplace.approved')
                        : m.status === 'REJECTED' ? t('admin.marketplace.rejected')
                          : m.status === 'SUBMITTED' ? t('admin.marketplace.submitted')
                            : m.status === 'APPROVED' ? t('admin.marketplace.approved')
                              : t('admin.marketplace.pending')}
                    </Badge>
                  </td>
                  <td>{new Date(m.createdAt).toLocaleDateString()}</td>
                  <td>
                    <Button variant="ghost" size="sm" onClick={() => openDetail(m.id)}>
                      {t('admin.marketplace.viewMerchant')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Onboarding wizard (hand-rolled multi-step) ── */}
      {showWizard && (
        <div className={styles.modalOverlay} onClick={() => setShowWizard(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.wizardTitle}>{t('admin.marketplace.onboardingTitle')}</h2>

            <div className={styles.stepIndicator}>
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`${styles.stepDot} ${wizard.step >= s ? styles.stepDotActive : ''} ${wizard.step > s ? styles.stepDotDone : ''}`}
                >
                  {wizard.step > s ? '✓' : s}
                </div>
              ))}
            </div>

            {wizard.error && <Alert tone="error" onDismiss={() => dispatch({ type: 'ERROR', error: '' })}>{wizard.error}</Alert>}

            {wizard.step === 1 && (
              <div className={styles.wizardStep}>
                <h3>{t('admin.marketplace.stepBusiness')}</h3>
                <FormField id="wiz-biz-name" label={t('admin.marketplace.businessName')} required>
                  <input id="wiz-biz-name" placeholder={t('admin.marketplace.businessNamePlaceholder')} value={wizard.businessName} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'businessName', value: e.target.value })} />
                </FormField>
                <FormField id="wiz-legal-name" label={t('admin.marketplace.legalName')} required>
                  <input id="wiz-legal-name" placeholder={t('admin.marketplace.legalNamePlaceholder')} value={wizard.legalName} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'legalName', value: e.target.value })} />
                </FormField>
                <FormField id="wiz-reg-num" label={t('admin.marketplace.businessRegNumber')}>
                  <input id="wiz-reg-num" placeholder={t('admin.marketplace.regNumberPlaceholder')} value={wizard.businessRegNumber} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'businessRegNumber', value: e.target.value })} />
                </FormField>
                <FormField id="wiz-tax-ref" label={t('admin.marketplace.taxReference')}>
                  <input id="wiz-tax-ref" placeholder={t('admin.marketplace.taxRefPlaceholder')} value={wizard.taxReference} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'taxReference', value: e.target.value })} />
                </FormField>
                <FormField id="wiz-type" label={t('admin.marketplace.merchantType')} required>
                  <Select
                    id="wiz-type"
                    value={wizard.merchantType}
                    onChange={(value) => dispatch({ type: 'SET_FIELD', field: 'merchantType', value })}
                    options={[
                      { value: 'INSTITUTION_SPONSORED', label: t('admin.marketplace.institutionSponsored') },
                      { value: 'PLATFORM', label: t('admin.marketplace.platform') },
                    ]}
                  />
                </FormField>
              </div>
            )}

            {wizard.step === 2 && (
              <div className={styles.wizardStep}>
                <h3>{t('admin.marketplace.stepContact')}</h3>
                <FormField id="wiz-email" label={t('admin.marketplace.contactEmail')} required>
                  <input id="wiz-email" type="email" placeholder={t('admin.marketplace.contactEmailPlaceholder')} value={wizard.email} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'email', value: e.target.value })} />
                </FormField>
                <FormField id="wiz-phone" label={t('admin.marketplace.contactPhone')} required>
                  <input id="wiz-phone" type="tel" placeholder={t('admin.marketplace.contactPhonePlaceholder')} value={wizard.phone} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'phone', value: e.target.value })} />
                </FormField>
                <FormField id="wiz-contact" label={t('admin.marketplace.contactName')} required>
                  <input id="wiz-contact" placeholder={t('admin.marketplace.contactNamePlaceholder')} value={wizard.contactName} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'contactName', value: e.target.value })} />
                </FormField>
                <FormField id="wiz-addr1" label={t('admin.marketplace.addressLine1')} required>
                  <input id="wiz-addr1" placeholder={t('admin.marketplace.address1Placeholder')} value={wizard.addressLine1} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'addressLine1', value: e.target.value })} />
                </FormField>
                <FormField id="wiz-addr2" label={t('admin.marketplace.addressLine2')}>
                  <input id="wiz-addr2" placeholder={t('admin.marketplace.address2Placeholder')} value={wizard.addressLine2} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'addressLine2', value: e.target.value })} />
                </FormField>
                <div className={styles.formRow}>
                  <FormField id="wiz-city" label={t('admin.marketplace.city')} required>
                    <input id="wiz-city" placeholder={t('admin.marketplace.cityPlaceholder')} value={wizard.city} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'city', value: e.target.value })} />
                  </FormField>
                  <FormField id="wiz-state" label={t('admin.marketplace.state')} required>
                    <Select
                      id="wiz-state"
                      searchable
                      disabled={regionOptions.length === 0}
                      placeholder={t('admin.marketplace.statePlaceholder')}
                      value={wizard.state}
                      onChange={(value) => dispatch({ type: 'SET_FIELD', field: 'state', value })}
                      options={regionOptions}
                    />
                  </FormField>
                  <FormField id="wiz-zip" label={t('admin.marketplace.postalCode')} required>
                    <input id="wiz-zip" placeholder={t('admin.marketplace.postalCodePlaceholder')} value={wizard.postalCode} onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'postalCode', value: e.target.value })} />
                  </FormField>
                </div>
                <FormField id="wiz-country" label={t('admin.marketplace.country')} required>
                  <Select
                    id="wiz-country"
                    searchable
                    placeholder={t('admin.marketplace.countryPlaceholder')}
                    value={wizard.country}
                    onChange={(value) => {
                      dispatch({ type: 'SET_FIELD', field: 'country', value });
                      // A region belongs to one country; keeping the old one
                      // would submit "Cameroon / Bavaria".
                      dispatch({ type: 'SET_FIELD', field: 'state', value: '' });
                    }}
                    options={countryOptions}
                  />
                </FormField>
              </div>
            )}

            {wizard.step === 3 && (
              <div className={styles.wizardStep}>
                <h3>{t('admin.marketplace.stepCampus')}</h3>
                {campuses.status === 'ready' && (
                  <Checklist
                    idPrefix="wiz-campus"
                    showSelectAll
                    selectAllLabel={t('admin.marketplace.selectAllCampuses', { defaultValue: 'Select all campuses' })}
                    items={campuses.data.map((c) => ({ value: c.id, label: c.name }))}
                    values={wizard.selectedCampuses}
                    onToggle={(id, checked) => {
                      const next = checked
                        ? [...wizard.selectedCampuses, id]
                        : wizard.selectedCampuses.filter((c) => c !== id);
                      dispatch({ type: 'SET_CAMPUSES', campuses: next });
                    }}
                  />
                )}
                {wizard.selectedCampuses.length === 0 && (
                  <p className={styles.hint}>{t('admin.marketplace.campusSelect')}</p>
                )}
              </div>
            )}

            {wizard.step === 4 && (
              <div className={styles.wizardStep}>
                <h3>{t('admin.marketplace.stepReview')}</h3>
                <div className={styles.reviewGrid}>
                  <div><strong>{t('admin.marketplace.businessName')}:</strong> {wizard.businessName}</div>
                  <div><strong>{t('admin.marketplace.legalName')}:</strong> {wizard.legalName}</div>
                  <div><strong>{t('admin.marketplace.contactEmail')}:</strong> {wizard.email}</div>
                  <div><strong>{t('admin.marketplace.contactPhone')}:</strong> {wizard.phone}</div>
                  <div><strong>{t('admin.marketplace.city')}:</strong> {wizard.city}, {wizard.state}</div>
                  <div><strong>{t('admin.marketplace.campusSelect')}:</strong> {wizard.selectedCampuses.length} campus selected</div>
                </div>
              </div>
            )}

            <div className={styles.wizardNav}>
              {wizard.step > 1 && (
                <Button variant="secondary" onClick={() => dispatch({ type: 'BACK' })}>
                  {t('common.back')}
                </Button>
              )}
              {wizard.step < 4 ? (
                <Button
                  variant="primary"
                  onClick={() => dispatch({ type: 'NEXT' })}
                  disabled={
                    (wizard.step === 1 && (!wizard.businessName || !wizard.legalName)) ||
                    (wizard.step === 2 && (!wizard.email || !wizard.phone || !wizard.addressLine1 || !wizard.city || !wizard.state || !wizard.postalCode || !wizard.country)) ||
                    (wizard.step === 3 && wizard.selectedCampuses.length === 0)
                  }
                >
                  {t('common.next')}
                </Button>
              ) : (
                <Button variant="primary" onClick={handleSubmitOnboarding} disabled={wizard.submitting}>
                  {wizard.submitting ? t('common.submitting') : t('admin.marketplace.submitApplication')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Invite marketplace role modal ── */}
      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={t('admin.marketplace.inviteRoleTitle')}
        closeOnBackdropClick={false}
      >
        <div
          className={styles.dialogForm}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault();
              if (inviteReady && !inviting) handleSendInvite();
            }
          }}
        >
          <FormField
            id="mkt-invite-email"
            label={t('admin.marketplace.inviteEmail')}
            required
            error={inviteEmailError || undefined}
          >
            <input
              id="mkt-invite-email"
              type="email"
              placeholder={t('admin.marketplace.inviteEmailPlaceholder')}
              value={inviteForm.email}
              aria-invalid={!!inviteEmailError}
              onChange={(e) => {
                setInviteForm({ ...inviteForm, email: e.target.value });
                if (inviteEmailError && emailLooksValid(e.target.value)) setInviteEmailError('');
              }}
              onBlur={() => {
                setInviteEmailError(
                  inviteForm.email && !emailLooksValid(inviteForm.email)
                    ? t('common.invalidEmail')
                    : '',
                );
              }}
            />
          </FormField>
          <FormField id="mkt-invite-role" label={t('admin.marketplace.selectRole')} required>
            <Select
              id="mkt-invite-role"
              searchable
              value={inviteForm.roleCode}
              onChange={(value) => {
                const role = inviteRoles.find((r) => r.code === value);
                // A merchant chosen for a merchant-scoped role means nothing for
                // an institution-scoped one; clear it rather than send it along.
                setInviteForm({
                  ...inviteForm,
                  roleCode: value,
                  merchantId: role?.scope === 'MERCHANT' ? inviteForm.merchantId : '',
                });
              }}
              placeholder={
                roleOptions.status === 'loading' && rolesRequested
                  ? t('admin.marketplace.loading')
                  : t('admin.marketplace.selectRole')
              }
              options={inviteRoles.map((r) => ({ value: r.code, label: r.displayName }))}
            />
          </FormField>
          {merchantRequired && (
            <FormField id="mkt-invite-merchant" label={t('admin.marketplace.selectMerchant')} required>
              <Select
                id="mkt-invite-merchant"
                searchable
                value={inviteForm.merchantId}
                onChange={(value) => setInviteForm({ ...inviteForm, merchantId: value })}
                placeholder={t('admin.marketplace.selectMerchant')}
                options={merchantList.map((m) => ({ value: m.id, label: m.businessName }))}
              />
            </FormField>
          )}
          <div className={styles.dialogActions}>
            <Button variant="secondary" onClick={() => setInviteOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSendInvite} loading={inviting} disabled={!inviteReady || inviting}>
              {t('admin.marketplace.sendInvite')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Mail-not-configured fallback: the exact invite link, ready to hand over. */}
      <Modal
        open={!!inviteShareLink}
        onClose={() => setInviteShareLink(null)}
        title={t('admin.users.inviteLinkTitle')}
        description={
          inviteShareLink ? t('admin.users.inviteLinkBody', { email: inviteShareLink.email }) : undefined
        }
        closeOnBackdropClick={false}
      >
        {inviteShareLink && (
          <div className={styles.dialogForm}>
            <input
              type="text"
              readOnly
              value={inviteShareLink.url}
              onFocus={(e) => e.target.select()}
              aria-label={t('admin.users.inviteLinkTitle')}
            />
            <div className={styles.dialogActions}>
              <Button variant="secondary" onClick={() => setInviteShareLink(null)}>
                {t('common.close')}
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(inviteShareLink.url);
                    toast.success(t('common.linkCopied'));
                  } catch {
                    toast.error(t('common.actionFailed'));
                  }
                }}
              >
                {t('common.copyLink')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Merchant detail modal ── */}
      <Modal
        open={!!detailMerchantId}
        onClose={closeDetail}
        title={detail?.data?.businessName ?? t('admin.marketplace.loading')}
      >
        {detail.status === 'loading' && (
          <div className={styles.modalLoading}>
            <Spinner label={t('admin.marketplace.loading')} />
          </div>
        )}

        {detail.status === 'ready' && detail.data && (
          <div className={styles.detailBody}>
            <div className={styles.detailGrid}>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>{t('admin.marketplace.legalName')}</span>
                <span>{detail.data.legalName}</span>
              </div>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>{t('admin.marketplace.merchantType')}</span>
                <span>{detail.data.merchantType}</span>
              </div>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>{t('admin.marketplace.approvalStatus')}</span>
                <Badge tone={STATUS_TONE[detail.data.status] ?? 'neutral'}>{detail.data.status}</Badge>
              </div>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>{t('admin.marketplace.contactEmail')}</span>
                <span>{detail.data.verifiedEmail}</span>
              </div>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>{t('admin.marketplace.contactPhone')}</span>
                <span>{detail.data.verifiedPhone}</span>
              </div>
              {detail.data.switchMerchantId && (
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>Switch ID</span>
                  <span className={styles.mono}>{detail.data.switchMerchantId}</span>
                </div>
              )}
              {detail.data.rejectionReason && (
                <div className={styles.detailField}>
                  <span className={styles.detailLabel}>{t('admin.marketplace.rejectionReason')}</span>
                  <span>{detail.data.rejectionReason}</span>
                </div>
              )}
            </div>

            {detail.data.profile && (
              <>
                <h4 className={styles.detailSectionTitle}>{t('admin.marketplace.stepContact')}</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailField}>
                    <span className={styles.detailLabel}>{t('admin.marketplace.addressLine1')}</span>
                    <span>{detail.data.profile.addressLine1}</span>
                  </div>
                  {detail.data.profile.addressLine2 && (
                    <div className={styles.detailField}>
                      <span className={styles.detailLabel}>{t('admin.marketplace.addressLine2')}</span>
                      <span>{detail.data.profile.addressLine2}</span>
                    </div>
                  )}
                  <div className={styles.detailField}>
                    <span className={styles.detailLabel}>{t('admin.marketplace.city')}</span>
                    <span>{detail.data.profile.city}, {detail.data.profile.state} {detail.data.profile.postalCode}</span>
                  </div>
                  <div className={styles.detailField}>
                    <span className={styles.detailLabel}>{t('admin.marketplace.country')}</span>
                    <span>{detail.data.profile.country}</span>
                  </div>
                  {detail.data.profile.contactName && (
                    <div className={styles.detailField}>
                      <span className={styles.detailLabel}>{t('admin.marketplace.contactName')}</span>
                      <span>{detail.data.profile.contactName}</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {detail.data.campusAssignments.length > 0 && (
              <>
                <h4 className={styles.detailSectionTitle}>{t('admin.marketplace.assignedCampuses')}</h4>
                <ul className={styles.detailCampusList}>
                  {detail.data.campusAssignments.map((a) => (
                    <li key={a.campusId}>{a.campusId} — <Badge tone="neutral">{a.status}</Badge></li>
                  ))}
                </ul>
              </>
            )}

            {detail.data.workflowCase && (
              <>
                <h4 className={styles.detailSectionTitle}>{t('admin.marketplace.stateHistory')}</h4>
                <div className={styles.stateHistory}>
                  {detail.data.workflowCase.stateHistory.map((h, i) => (
                    <div key={i} className={styles.stateEntry}>
                      <span className={styles.stateArrow}>{h.from || '…'} → {h.to}</span>
                      <span className={styles.stateMeta}>{new Date(h.at).toLocaleString()}{h.reason ? ` — ${h.reason}` : ''}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {actionError && (
              <Alert tone="error" onDismiss={() => setActionError(null)}>{actionError}</Alert>
            )}

            {(detail.data.status === 'SUBMITTED') && (
              <div className={styles.detailActions}>
                <div className={styles.approveSection}>
                  <FormField id="owner-email" label={t('admin.marketplace.ownerEmail')} required>
                    <input
                      type="email"
                      value={ownerEmail}
                      onChange={(e) => setOwnerEmail(e.target.value)}
                      placeholder={t('admin.marketplace.ownerEmailPlaceholder')}
                    />
                  </FormField>
                  <Button
                    variant="primary"
                    onClick={handleApprove}
                    disabled={actionLoading || !ownerEmail.trim()}
                    loading={actionLoading}
                  >
                    {t('admin.marketplace.approve')}
                  </Button>
                </div>
                <div className={styles.rejectSection}>
                  <FormField id="reject-reason" label={t('admin.marketplace.rejectionReason')} required>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder={t('admin.marketplace.rejectionReasonPlaceholder')}
                      rows={2}
                    />
                  </FormField>
                  <Button
                    variant="danger"
                    onClick={handleReject}
                    disabled={actionLoading || !rejectReason.trim()}
                    loading={actionLoading}
                  >
                    {t('admin.marketplace.reject')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {detail.status === 'error' && (
          <Alert tone="error">{detail.error?.message ?? 'Failed to load merchant details'}</Alert>
        )}
      </Modal>
    </div>
  );
}
